const calculateNIC = (gross, frequency = 'monthly') => {
  console.log('Frequency:', frequency);
  console.log('Gross Salary:', gross);
  // Thresholds for 2025–2026
  const thresholds = {
    monthly: {
      pt: 1048,
      uel: 4189
    },
    weekly: {
      pt: 242,
      uel: 967
    }
  };

  if (!thresholds[frequency]) {
    console.warn('Invalid frequency in formData.nationalInsurance');
    return 0;
  }

  const { pt, uel } = thresholds[frequency];

  let nic = 0;

  if (gross <= pt) {
    nic = 0;
  } else if (gross <= uel) {
    nic = (gross - pt) * 0.1;
  } else {
    const band1 = (uel - pt) * 0.1;
    const band2 = (gross - uel) * 0.02;
    nic = band1 + band2;
  }

  return parseFloat(nic.toFixed(2));
};
const calculatePAYE = (gross, tax, salaryType) => {
  const frequency = salaryType === 'monthly' ? 'monthly' : 'weekly';
  const taxCode = tax || '1257L';
  const salary = parseFloat(gross) || 0;

  // Step 1: Get personal allowance
  let allowanceAnnual = 0;

  // Standard case (1257L → £12,570)
  if (/^(\d{4})L$/.test(taxCode)) {
    const match = taxCode.match(/^(\d{4})L$/);
    allowanceAnnual = parseInt(match[1]) * 10;
  } else if (taxCode === '0T' || taxCode === 'BR') {
    allowanceAnnual = 0; // No allowance
  } else {
    allowanceAnnual = 12570; // Default fallback
  }

  // Reduce allowance for high earners (>£100,000)
  const annualSalary = frequency === 'monthly' ? salary * 12 : salary * 52;
  if (annualSalary > 100000) {
    const reduction = Math.floor((annualSalary - 100000) / 2);
    allowanceAnnual = Math.max(0, allowanceAnnual - reduction);
  }

  const allowancePeriod =
    frequency === 'monthly' ? allowanceAnnual / 12 : allowanceAnnual / 52;

  // Step 2: Calculate taxable income
  const taxable = Math.max(0, salary - allowancePeriod);

  // Step 3: Apply PAYE bands
  const bands = {
    monthly: [
      { to: 4189, rate: 0.2 },
      { to: 10428, rate: 0.4 },
      { to: Infinity, rate: 0.45 }
    ],
    weekly: [
      { to: 967, rate: 0.2 },
      { to: 2401, rate: 0.4 },
      { to: Infinity, rate: 0.45 }
    ]
  };

  const bandSet = bands[frequency];
  let paye = 0;
  let remaining = taxable;

  let lower = 0;
  for (const band of bandSet) {
    if (remaining <= 0) break;
    const bandWidth = band.to - lower;
    const amount = Math.min(remaining, bandWidth);
    paye += amount * band.rate;
    remaining -= amount;
    lower = band.to;
  }

  return parseFloat(paye.toFixed(2));
};
const calculateNetPay = (gross, tax, salaryType) => {
  // const frequency = salaryType === 'monthly' ? 'monthly' : 'weekly';
  const salary = parseFloat(gross) || 0;
  const paye = calculatePAYE(salary, tax, salaryType);
  const nic = calculateNIC(salary, salaryType);
  const pension = 0; // Assuming no pension contributions for simplicity
  const otherDeductions = 0; // Assuming no other deductions for simplicity
  const netPay = salary - paye - nic - pension - otherDeductions;
  return parseFloat(netPay.toFixed(2));
};
const jsonSchema = [
  {
    type: 'group',
    fields: [
      {
        name: 'lengthOfService',
        label: 'Lenght of Service',
        required: true,
        type: 'text'
      },
      {
        name: 'jobType',
        label: 'Job Type',
        required: true,
        type: 'text'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'startDate',
        label: 'Start Date',
        required: true,
        type: 'date'
      },
      {
        name: 'country',
        label: 'Country',
        required: false,
        type: 'text'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'contService',
        label: 'Cont Service',
        required: true,
        type: 'text'
      },
      {
        name: 'location',
        label: 'Location',
        required: false,
        type: 'text'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'benefitsStart',
        label: 'Benefits Start',
        required: false,
        type: 'date'
      },
      {
        name: 'department',
        label: 'Department',
        required: false,
        type: 'text'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'noticePeriod',
        label: 'Notice Period',
        required: false,
        type: 'text'
      },
      {
        name: 'myLineManager',
        label: 'My Line Manager',
        required: true,
        type: 'text'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'rightToWork',
        label: 'Right to Work',
        required: false,
        type: 'date'
      },
      {
        name: 'rightToWorkExpiry',
        label: 'Right to Work Expiry',
        required: false,
        type: 'date'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'hoursPerWeek',
        label: 'Hours per Week',
        required: false,
        type: 'text'
      },
      {
        name: 'holidayEntitlement',
        label: 'Holiday Entitlement',
        required: true,
        type: 'number'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'daysPerWeek',
        label: 'Days per Week',
        required: false,
        type: 'text'
      },
      {
        name: 'holidayBroughtForward',
        label: 'Holiday Brought Forward',
        required: true,
        type: 'number'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'fte',
        label: 'FTE',
        required: false,
        type: 'number'
      },
      {
        name: 'broughtForwardScheme',
        label: 'Brought Forward Scheme (*)',
        required: true,
        type: 'number'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'workPattern',
        label: 'Work Pattern',
        required: false,
        type: 'text'
      },
      {
        name: 'holidayTotalThisYear',
        label: 'Holiday Total This Year',
        required: false,
        disabled: true,
        type: 'number',
        computed: true,
        computedValue: formData => {
          const holidayBroughtForward =
            parseFloat(formData.holidayBroughtForward) || 0;
          const holidayEntitlement =
            parseFloat(formData.holidayEntitlement) || 0;

          return (holidayBroughtForward + holidayEntitlement).toFixed(2);
        }
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'nationalInsuranceNo',
        label: 'National Insurance No.',
        required: true,
        type: 'text'
      },
      {
        name: 'salaryType',
        label: 'Salary Type',
        required: true,
        type: 'multiselect',

        options: [
          { label: 'monthly', value: 'monthly' },
          { label: 'weekly', value: 'weekly' }
        ]
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'grossSalary',
        label: 'Gross Salary',
        required: true,
        type: 'currency'
      },
      {
        name: 'taxCode',
        label: 'Tax Code',
        required: true,
        type: 'text'
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'payeDeduction',
        label: 'PAYE Deduction',
        required: false,
        type: 'currency',
        disabled: true,
        computed: true,
        computedValue: formData => {
          return calculatePAYE(
            parseFloat(formData.grossSalary),
            formData.taxCode,
            'monthly'
          ).toFixed(2);
        }
      },
      {
        name: 'nationalInsurance',
        label: 'National Insurance',
        required: false,
        type: 'currency',
        disabled: true,
        computed: true,
        computedValue: formData => {
          console.log(formData.grossSalary);
          return parseFloat(
            calculateNIC(parseFloat(formData.grossSalary), 'monthly').toFixed(2)
          );
        }
      }
    ]
  },
  {
    type: 'group',
    fields: [
      {
        name: 'pensionContributions',
        label: 'Pension Contributions',
        required: false,
        type: 'currency'
      },
      {
        name: 'otherDeductions',
        label: 'Other Deductions',
        required: false,
        type: 'currency'
      }
    ]
  },
  {
    name: 'netPay',
    label: 'Net Pay',
    required: true,
    type: 'currency',
    disabled: true,
    computed: true,
    computedValue: formData => {
      return calculateNetPay(
        formData.grossSalary,
        formData.taxCode,
        'monthly'
      ).toFixed(2);
    }
  }
];
export default jsonSchema;
